import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaComments, FaTimes, FaPaperPlane, FaRobot, FaPhone, FaWhatsapp, FaEnvelope } from 'react-icons/fa';
import './Chatbot.css';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Load chat history from localStorage on mount
  useEffect(() => {
    const savedMessages = localStorage.getItem('chatHistory');
    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    } else {
      // Initial welcome message
      const welcomeMessage = {
        type: 'bot',
        text: 'Hello! Welcome to Prime Crown Assets. How can I help you today?',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages([welcomeMessage]);
    }
  }, []);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('chatHistory', JSON.stringify(messages));
    }
  }, [messages]);

  const quickReplies = [
    'Services',
    'Contact Info',
    'DTCP Layouts',
    'Construction',
    'Pricing',
    'Location'
  ];

  const servicesDetails = {
    'dtcp': {
      name: 'DTCP Approved Layout Design',
      description: 'Professional master planning and site layout design for residential and commercial developments',
      features: [
        'Complete DTCP approval assistance',
        'Site survey and analysis',
        'Road and drainage planning',
        'Plot demarcation',
        'Legal documentation support',
        'Multiple layout options'
      ],
      benefits: 'Fully approved layouts with clear documentation and prime locations across Coimbatore'
    },
    'construction': {
      name: 'Building Construction',
      description: 'High-quality residential and commercial building construction services',
      features: [
        'Residential homes and villas',
        'Commercial buildings',
        'Apartments and complexes',
        'Custom architectural designs',
        'Quality materials',
        'Timely project delivery',
        'Post-construction support'
      ],
      benefits: 'End-to-end construction with experienced engineers and quality assurance'
    },
    'land': {
      name: 'Land Sale & Purchase',
      description: 'Trusted land sale and purchase services with complete legal verification',
      features: [
        'DTCP approved plots',
        'Prime locations in Coimbatore',
        'Clear title verification',
        'Legal documentation',
        'Flexible payment options',
        'Site visit arrangements'
      ],
      benefits: 'Safe and secure land transactions with complete transparency'
    },
    'documentation': {
      name: 'Property Documentation',
      description: 'Complete property documentation and legal services',
      features: [
        'Patta transfer',
        'Property registration',
        'DTCP approvals',
        'Mutation services',
        'EC (Encumbrance Certificate)',
        'Legal verification',
        'Title deed services'
      ],
      benefits: 'Hassle-free documentation with expert legal support'
    },
    'interior': {
      name: 'Interior & Exterior Design',
      description: 'Creative interior and exterior design services',
      features: [
        '3D visualization',
        'Space planning',
        'Material selection',
        'Furniture design',
        'Lighting design',
        'Color consultation',
        'Landscape design'
      ],
      benefits: 'Transform your space with modern and functional designs'
    },
    'renovation': {
      name: 'Renovation & Remodeling',
      description: 'Professional renovation and remodeling services',
      features: [
        'Home renovation',
        'Kitchen remodeling',
        'Bathroom upgrades',
        'Structural modifications',
        'Painting and finishing',
        'Electrical and plumbing updates'
      ],
      benefits: 'Breathe new life into your property with expert renovation'
    },
    'project': {
      name: 'Project Management',
      description: 'End-to-end construction project management',
      features: [
        'Project planning',
        'Budget management',
        'Timeline coordination',
        'Quality control',
        'Vendor management',
        'Progress reporting',
        'Risk management'
      ],
      benefits: 'Professional project management ensuring timely and quality delivery'
    }
  };

  const botResponses = {
    services: {
      keywords: ['service', 'services', 'what do you do', 'offerings', 'provide', 'all services'],
      response: 'We offer comprehensive real estate and construction services:\n\n🏗️ DTCP Approved Layout Design\n🏠 Building Construction\n🏡 Land Sale & Purchase\n📄 Property Documentation\n🎨 Interior & Exterior Design\n🔧 Renovation & Remodeling\n📊 Project Management\n\nType the service name for detailed information!'
    },
    contact: {
      keywords: ['contact', 'phone', 'email', 'reach', 'call', 'address', 'location', 'where'],
      response: 'contact_info'
    },
    dtcp: {
      keywords: ['dtcp', 'layout', 'plot', 'land', 'approved', 'layout design'],
      response: () => {
        const service = servicesDetails.dtcp;
        return `${service.name}\n\n${service.description}\n\n✨ Features:\n${service.features.map(f => `• ${f}`).join('\n')}\n\n💡 ${service.benefits}\n\nInterested? Contact us for more details!`;
      }
    },
    construction: {
      keywords: ['construction', 'building', 'build', 'house', 'home', 'villa', 'construct'],
      response: () => {
        const service = servicesDetails.construction;
        return `${service.name}\n\n${service.description}\n\n✨ Features:\n${service.features.map(f => `• ${f}`).join('\n')}\n\n💡 ${service.benefits}\n\nLet's discuss your project!`;
      }
    },
    land: {
      keywords: ['land sale', 'land purchase', 'buy land', 'sell land', 'property sale'],
      response: () => {
        const service = servicesDetails.land;
        return `${service.name}\n\n${service.description}\n\n✨ Features:\n${service.features.map(f => `• ${f}`).join('\n')}\n\n💡 ${service.benefits}\n\nReady to invest? Contact us!`;
      }
    },
    documentation: {
      keywords: ['document', 'documentation', 'paper', 'legal', 'registration', 'patta', 'mutation'],
      response: () => {
        const service = servicesDetails.documentation;
        return `${service.name}\n\n${service.description}\n\n✨ Services:\n${service.features.map(f => `• ${f}`).join('\n')}\n\n💡 ${service.benefits}`;
      }
    },
    interior: {
      keywords: ['interior', 'exterior', 'design', 'decoration', 'furnish'],
      response: () => {
        const service = servicesDetails.interior;
        return `${service.name}\n\n${service.description}\n\n✨ Services:\n${service.features.map(f => `• ${f}`).join('\n')}\n\n💡 ${service.benefits}`;
      }
    },
    renovation: {
      keywords: ['renovation', 'remodel', 'upgrade', 'modify', 'repair'],
      response: () => {
        const service = servicesDetails.renovation;
        return `${service.name}\n\n${service.description}\n\n✨ Services:\n${service.features.map(f => `• ${f}`).join('\n')}\n\n💡 ${service.benefits}`;
      }
    },
    project: {
      keywords: ['project management', 'manage', 'coordination', 'planning'],
      response: () => {
        const service = servicesDetails.project;
        return `${service.name}\n\n${service.description}\n\n✨ Services:\n${service.features.map(f => `• ${f}`).join('\n')}\n\n💡 ${service.benefits}`;
      }
    },
    pricing: {
      keywords: ['price', 'cost', 'budget', 'rate', 'charges', 'fees', 'expensive', 'cheap', 'quote'],
      response: 'Our pricing varies based on:\n\n💰 Project type and size\n💰 Location\n💰 Materials and specifications\n💰 Timeline\n\nFor accurate pricing, please contact us:\n📞 +91 97911 77445\n📧 primecrownassets@gmail.com\n\nWe offer competitive rates and flexible payment plans!'
    },
    greeting: {
      keywords: ['hi', 'hello', 'hey', 'good morning', 'good afternoon', 'good evening', 'namaste'],
      response: 'Hello! 👋 Welcome to Prime Crown Assets!\n\nWe are your trusted real estate partner in Coimbatore. How can I assist you today?'
    },
    thanks: {
      keywords: ['thank', 'thanks', 'appreciate', 'grateful'],
      response: 'You\'re welcome! 😊\n\nIf you have any more questions, feel free to ask. We\'re here to help!\n\n📞 Call us: +91 97911 77445'
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getBotResponse = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();

    // Check for contact info request
    if (botResponses.contact.keywords.some(keyword => lowerMessage.includes(keyword))) {
      return 'contact_info';
    }

    // Check each category for keyword matches
    for (const data of Object.values(botResponses)) {
      if (data.keywords && data.keywords.some(keyword => lowerMessage.includes(keyword))) {
        return typeof data.response === 'function' ? data.response() : data.response;
      }
    }

    // Default response if no match found
    return 'I\'m here to help! 🤖\n\nYou can ask me about:\n• Our Services (DTCP, Construction, Land Sale, etc.)\n• Contact Information\n• Pricing\n• Documentation\n\nOr call us directly at +91 97911 77445';
  };

  const handleSendMessage = () => {
    if (inputValue.trim() === '') return;

    const userMessage = {
      type: 'user',
      text: inputValue,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate bot typing delay
    setTimeout(() => {
      const response = getBotResponse(inputValue);
      const botMessage = {
        type: 'bot',
        text: response,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isContactInfo: response === 'contact_info'
      };
      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 1000);
  };

  const handleQuickReply = (reply) => {
    setInputValue(reply);
    setTimeout(() => handleSendMessage(), 100);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChatHistory = () => {
    localStorage.removeItem('chatHistory');
    const welcomeMessage = {
      type: 'bot',
      text: 'Chat history cleared! How can I help you today?',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages([welcomeMessage]);
  };

  const renderMessage = (message) => {
    if (message.isContactInfo) {
      return (
        <div className="contact-info-card">
          <h4>📍 Contact Information</h4>
          
          <div className="contact-item">
            <FaPhone className="contact-icon-small" />
            <div>
              <strong>Phone</strong>
              <a href="tel:+919791177445" className="contact-link">
                +91 97911 77445
              </a>
            </div>
          </div>

          <div className="contact-item">
            <FaWhatsapp className="contact-icon-small whatsapp" />
            <div>
              <strong>WhatsApp</strong>
              <a 
                href="https://wa.me/919791177445?text=Hello%20Prime%20Crown%20Assets!%0A%0AI%20am%20interested%20in%20your%20services." 
                target="_blank" 
                rel="noopener noreferrer"
                className="contact-link whatsapp"
              >
                Chat on WhatsApp
              </a>
            </div>
          </div>

          <div className="contact-item">
            <FaEnvelope className="contact-icon-small" />
            <div>
              <strong>Email</strong>
              <a href="mailto:primecrownassets@gmail.com" className="contact-link">
                primecrownassets@gmail.com
              </a>
            </div>
          </div>

          <div className="contact-item address">
            <div>
              <strong>📍 Address</strong>
              <p>Thulasiram Plaza, SamiChettipalayam Road,<br />Vannan Kovil Pirivu, Coimbatore-641047</p>
            </div>
          </div>

          <div className="contact-item">
            <div>
              <strong>⏰ Business Hours</strong>
              <p>Monday - Saturday: 9:00 AM - 6:00 PM<br />Sunday: Closed</p>
            </div>
          </div>
        </div>
      );
    }
    return message.text;
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            className="chatbot-toggle"
            onClick={() => setIsOpen(true)}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaComments />
            <span className="chat-badge">1</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="chatbot-window"
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            {/* Chat Header */}
            <div className="chatbot-header">
              <div className="chatbot-header-info">
                <div className="chatbot-avatar">
                  <FaRobot />
                </div>
                <div>
                  <h4>Prime Crown Assistant</h4>
                  <span className="chatbot-status">
                    <span className="status-dot"></span> Online
                  </span>
                </div>
              </div>
              <div className="header-actions">
                <button 
                  className="clear-history-btn" 
                  onClick={clearChatHistory}
                  title="Clear chat history"
                >
                  🗑️
                </button>
                <button className="chatbot-close" onClick={() => setIsOpen(false)}>
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="chatbot-messages">
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  className={`message ${message.type}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {message.type === 'bot' && (
                    <div className="message-avatar">
                      <FaRobot />
                    </div>
                  )}
                  <div className="message-content">
                    <div className="message-text">
                      {renderMessage(message)}
                    </div>
                    <div className="message-time">{message.time}</div>
                  </div>
                </motion.div>
              ))}

              {isTyping && (
                <motion.div
                  className="message bot"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="message-avatar">
                    <FaRobot />
                  </div>
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick Replies */}
            {messages.length <= 2 && (
              <div className="quick-replies">
                {quickReplies.map((reply, index) => (
                  <motion.button
                    key={index}
                    className="quick-reply-btn"
                    onClick={() => handleQuickReply(reply)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {reply}
                  </motion.button>
                ))}
              </div>
            )}

            {/* Chat Input */}
            <div className="chatbot-input">
              <input
                type="text"
                placeholder="Type your message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <button
                className="send-button"
                onClick={handleSendMessage}
                disabled={inputValue.trim() === ''}
              >
                <FaPaperPlane />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Chatbot;
